<!--@format-->
<template>
    <div class="account-details-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Company Details</h3>
                <div class="svg-container widget-close-button" @click="closeWidget()">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <loading-svg v-if="loading" />
                <div v-else>
                    <signup-referral-question v-model="referral.answer" editable />
                    <marketing-experience-question v-model="experience.answer" editable />
                    <marketing-channels-question v-model="marketingChannels.answer" editable />
                    <role-question v-model="role.answer" editable />
                    <employees-question v-model="employees.answer" editable />
                    <ms-employees-question v-model="msEmployees.answer" editable />
                    <interest-question v-model="interest.answer" editable />
                    <psa-question v-model="psa.answer" editable />
                    <vendors-question v-model="vendors.answer" editable />
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="closeWidget()"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button @click="save()"> Save </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const AppButton = () => import('@/components/common/app-button');
const LoadingSvg = () => import('@/components/svg/loading-screen-svg');
const MarketingExperienceQuestion = () =>
    import('@/components/sliding-widgets/account-details-widget/marketing-experience-question');
const MarketingChannelsQuestion = () =>
    import('@/components/sliding-widgets/account-details-widget/marketing-channels-question');
const RoleQuestion = () =>
    import('@/components/sliding-widgets/account-details-widget/role-question');
const EmployeesQuestion = () =>
    import('@/components/sliding-widgets/account-details-widget/employees-question');
const MSEmployeesQuestion = () =>
    import('@/components/sliding-widgets/account-details-widget/ms-employees-question');
const InterestQuestion = () =>
    import('@/components/sliding-widgets/account-details-widget/interest-question');
const PsaQuestion = () =>
    import('@/components/sliding-widgets/account-details-widget/psa-question');
const VendorsQuestion = () =>
    import('@/components/sliding-widgets/account-details-widget/vendors-question');
const SignupReferralQuestion = () =>
    import('@/components/sliding-widgets/account-details-widget/signup-referral-question');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'account-details-widget',

    components: {
        'widget-close-svg': WidgetCloseSvg,
        'app-button': AppButton,
        'loading-svg': LoadingSvg,
        'marketing-experience-question': MarketingExperienceQuestion,
        'marketing-channels-question': MarketingChannelsQuestion,
        'role-question': RoleQuestion,
        'employees-question': EmployeesQuestion,
        'ms-employees-question': MSEmployeesQuestion,
        'interest-question': InterestQuestion,
        'psa-question': PsaQuestion,
        'vendors-question': VendorsQuestion,
        'signup-referral-question': SignupReferralQuestion,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            accountId: this.widget.accountId,
            referral: {
                key: 'referral',
                question: 'How did you hear about GlassHive?',
                answer: null,
            },

            experience: {
                key: 'experience',
                question: 'What is your level of marketing experience?',
                answer: null,
            },
            marketingChannels: {
                key: 'marketing-channels',
                question: 'Select what marketing channels you have personal experience with:',
                answer: [],
            },
            role: {
                key: 'role',
                question: 'What is your role in the company?',
                answer: null,
            },
            employees: {
                key: 'employees',
                question: 'How many employees work at your company?',
                answer: null,
            },
            msEmployees: {
                key: 'ms-employees',
                question: 'How many employees are sales and/or marketing specific?',
                answer: null,
            },
            interest: {
                key: 'interest',
                question: 'What in GlassHive are you most interested in?',
                answer: null,
            },
            psa: {
                key: 'psa',
                question: 'Which PSA are you using?',
                answer: null,
            },
            vendors: {
                key: 'vendors',
                question: 'What vendors is your business using?',
                answer: [],
            },
            loading: false,
        };
    },

    created() {
        this.getData();
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        save() {
            this.loading = true;

            var request = {
                Json: JSON.stringify({
                    questions: [
                        this.referral,
                        this.experience,
                        this.marketingChannels,
                        this.role,
                        this.employees,
                        this.msEmployees,
                        this.interest,
                        this.psa,
                        this.vendors,
                    ],
                }),
            };

            this.$api
                .put(`/api/Account/${this.accountId}/Persona`, request)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        `Successfully updated account information`
                    );
                    EventBus.$emit('account:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(`Failed to update account information`, { error });
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        getData() {
            this.loading = true;

            this.$api
                .get(`/api/Account/${this.accountId}/Persona`)
                .then((response) => {
                    if (response.data) {
                        var personaJson = JSON.parse(response.data);
                        var personaQuestions = personaJson.questions || [];

                        var experienceQuestion = personaQuestions.find(
                            (q) =>
                                q.key === this.experience.key ||
                                q.question === this.experience.question
                        );

                        if (experienceQuestion) {
                            this.experience.answer = experienceQuestion.answer;
                        }

                        var signupReferralQuestion = personaQuestions.find(
                            (q) =>
                                q.key === this.referral.key || q.question === this.referral.question
                        );

                        if (signupReferralQuestion) {
                            this.referral.answer = signupReferralQuestion.answer;
                        }

                        var marketingChannelsQuestion = personaQuestions.find(
                            (q) =>
                                q.key === this.marketingChannels.key ||
                                q.question === this.marketingChannels.question
                        );

                        if (marketingChannelsQuestion) {
                            this.marketingChannels.answer = marketingChannelsQuestion.answer.map(
                                (channel) => channel
                            );
                        }

                        var roleQuestion = personaQuestions.find(
                            (q) => q.key === this.role.key || q.question === this.role.question
                        );

                        if (roleQuestion) {
                            this.role.answer = roleQuestion.answer;
                        }

                        var employeesQuestion = personaQuestions.find(
                            (q) =>
                                q.key === this.employees.key ||
                                q.question === this.employees.question
                        );

                        if (employeesQuestion) {
                            this.employees.answer = employeesQuestion.answer;
                        }

                        var msEmployeesQuestion = personaQuestions.find(
                            (q) =>
                                q.key === this.msEmployees.key ||
                                q.question === this.msEmployees.question
                        );

                        if (msEmployeesQuestion) {
                            this.msEmployees.answer = msEmployeesQuestion.answer;
                        }

                        var interestQuestion = personaQuestions.find(
                            (q) =>
                                q.key === this.interest.key || q.question === this.interest.question
                        );

                        if (interestQuestion) {
                            this.interest.answer = interestQuestion.answer;
                        }

                        var psaQuestion = personaQuestions.find(
                            (q) => q.key === this.psa.key || q.question === this.psa.question
                        );

                        if (psaQuestion) {
                            this.psa.answer = psaQuestion.answer;
                        }

                        var vendorsQuestion = personaQuestions.find(
                            (q) =>
                                q.key === this.vendors.key || q.question === this.vendors.question
                        );

                        if (vendorsQuestion) {
                            this.vendors.answer = vendorsQuestion.answer.map((vendor) => vendor);
                        }
                    }
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(`Failed to get account information`, { error });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>

<style>
.account-details-widget.widget .content-wrapper {
    padding: 30px 35px;
}

.account-details-widget .loading-screen {
    width: 100%;
    text-align: center;
}

.account-details-widget .app-input {
    margin-bottom: 15px;
}

.account-details-widget .transparent-btn {
    background: transparent;
    color: #877d88;
}

.account-details-widget .transparent-btn:hover {
    background: #e1e0e5;
}

.account-details-widget .subtext {
    display: none;
}
</style>
